import { Assets, type Spritesheet, type Texture } from "pixi.js";

/**
 * Owns Pixi spritesheet handles by alias and lets gameplay code resolve
 * frames / animations by name without touching `Assets` directly.
 *
 *   await atlas.load("character", "/atlases/character.json");
 *   const tex = atlas.getTexture("character", "char-idle-down-0");
 *   const frames = atlas.getAnimation("character", "char-walk-down");
 *
 * The atlas JSON must follow Pixi's standard spritesheet format (see
 * `pixi.js`'s `Spritesheet` class). Animations are picked up automatically.
 *
 * In dev, Vite's HMR triggers `import.meta.hot` for assets, but Pixi caches
 * spritesheets in its global Assets registry — call `reload()` to clear and
 * re-fetch when iterating on art.
 */
export class TextureAtlasManager {
  private sheets = new Map<string, Spritesheet>();
  private urls = new Map<string, string>();

  async load(alias: string, url: string): Promise<Spritesheet> {
    const cached = this.sheets.get(alias);
    if (cached) return cached;
    Assets.add({ alias, src: url });
    const sheet = await Assets.load<Spritesheet>(alias);
    // Pixel-art crisp: switch the underlying texture source to nearest-neighbor
    // sampling so movement at sub-pixel positions doesn't bilinear-blur sprites.
    const source = (sheet.textureSource ?? sheet.textures[Object.keys(sheet.textures)[0] ?? ""]?.source) as
      | { scaleMode?: string }
      | undefined;
    if (source) source.scaleMode = "nearest";
    this.sheets.set(alias, sheet);
    this.urls.set(alias, url);
    return sheet;
  }

  has(alias: string): boolean {
    return this.sheets.has(alias);
  }

  /** Returns the named frame texture or throws if missing. */
  getTexture(alias: string, frameName: string): Texture {
    const sheet = this.requireSheet(alias);
    const tex = sheet.textures[frameName];
    if (!tex) throw new Error(`atlas "${alias}" has no frame "${frameName}"`);
    return tex;
  }

  /** Returns the named animation (array of frame textures) or throws. */
  getAnimation(alias: string, animationName: string): Texture[] {
    const sheet = this.requireSheet(alias);
    const anim = sheet.animations[animationName];
    if (!anim) throw new Error(`atlas "${alias}" has no animation "${animationName}"`);
    return anim;
  }

  /** Best-effort lookup for frame OR animation. Returns undefined if missing. */
  tryGetTextures(alias: string, key: string): Texture[] | undefined {
    const sheet = this.sheets.get(alias);
    if (!sheet) return undefined;
    if (sheet.animations[key]) return sheet.animations[key];
    const t = sheet.textures[key];
    return t ? [t] : undefined;
  }

  /** All registered atlas aliases. */
  list(): string[] {
    return [...this.sheets.keys()];
  }

  /** Drops the sheet from the manager and from Pixi.Assets cache. */
  async unload(alias: string): Promise<void> {
    if (!this.sheets.has(alias)) return;
    await Assets.unload(alias);
    this.sheets.delete(alias);
    this.urls.delete(alias);
  }

  /** Reloads an atlas (used by HMR in dev). */
  async reload(alias: string): Promise<Spritesheet> {
    const url = this.urls.get(alias);
    if (!url) throw new Error(`cannot reload unknown atlas "${alias}"`);
    await this.unload(alias);
    return this.load(alias, url);
  }

  async unloadAll(): Promise<void> {
    for (const alias of [...this.sheets.keys()]) await this.unload(alias);
  }

  private requireSheet(alias: string): Spritesheet {
    const sheet = this.sheets.get(alias);
    if (!sheet) throw new Error(`atlas "${alias}" not loaded`);
    return sheet;
  }
}
